import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    DealershipPicker,
    useDealershipSelector as useDealershipPickerSelector
} from '@makemydeal/dr-activities-dealership-picker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { useDealershipSelector, updateDealerDetails, fetchInitialConfigAction } from '@makemydeal/dr-sales-checkin-store';
import { StyledDealershipCard, StyledDealershipLabel } from './DealSetupScreen.style';

/**
 * DealSetupDealershipSection — Dealership selector for the Deal Setup screen.
 *
 * Responsibilities:
 * 1. Auto-select dealership if currentDealership exists (from bootstrap/invalid deal)
 * 2. Fetch dealerSettings when user manually changes dealership
 * 3. Show inline loader while fetching dealer settings
 *
 * Note: When a dealership is pre-selected (from bootstrap/invalid deal), dealer settings
 * are fetched during init. This component only fetches on manual dealership change.
 */
export const DealSetupDealershipSection: React.FC = () => {
    const dispatch = useDispatch();
    const [selectedDealerMmdId, setSelectedDealerMmdId] = useState<string>('');

    const { currentDealership, availableDealerships } = useDealershipSelector();
    const { dealerships } = useDealershipPickerSelector();

    // Auto-select dealership dropdown if currentDealership exists
    useEffect(() => {
        if (!selectedDealerMmdId && currentDealership?.dealerMMDId) {
            setSelectedDealerMmdId(`${currentDealership.dealerMMDId}`);
        }
    }, [currentDealership?.dealerMMDId, selectedDealerMmdId]);

    const handleChangeDealership = useCallback(
        (e: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const selectedDealerMmdIdValue = e.target.value as string;

            // Skip fetch if the same dealership is re-selected
            if (selectedDealerMmdIdValue === selectedDealerMmdId) {
                return;
            }

            setSelectedDealerMmdId(selectedDealerMmdIdValue);

            const allDealerships = dealerships.length > 0 ? dealerships : availableDealerships;
            const selectedDealership = allDealerships.find(
                (dealership) => `${dealership.dealerMMDId}` === selectedDealerMmdIdValue
            );

            if (selectedDealership?.dealerMMDId) {
                // Clear dealerSettings while API is in-flight, then fetch fresh settings
                dispatch(updateDealerDetails({ ...selectedDealership, dealerSettings: undefined } as any));
                dispatch(fetchInitialConfigAction(`${selectedDealership.dealerMMDId}`));
            }
        },
        [dispatch, dealerships, availableDealerships, selectedDealerMmdId]
    );

    return (
        <StyledDealershipCard>
            <StyledDealershipLabel>Dealership (required)</StyledDealershipLabel>
            <DealershipPicker onChangeDealership={handleChangeDealership} value={selectedDealerMmdId} />
        </StyledDealershipCard>
    );
};
